/**
 * In-house replacement for vue-drag-drop. The original package was a
 * thin wrapper around HTML5 DnD events; that's what these two components
 * are too. Lets us drop the dependency without touching the 3 quiz
 * question-model templates that use `<drag>` / `<drop>`.
 *
 * API mirrored from vue-drag-drop (the small subset the codebase uses):
 *
 *   <drag :transfer-data="payload" @dragstart="..." @dragend="...">
 *     ...
 *   </drag>
 *
 *   <drop @drop="(data, ev) => ...">
 *     ...
 *   </drop>
 *
 * `transferData` is JSON-encoded via `dataTransfer.setData('text/plain')`
 * and decoded back on the other side. Non-serialisable values are passed
 * by reference via a module-local map keyed on a UUID stored in the
 * dataTransfer payload — works inside one window/tab, fine for our use
 * case (no cross-window DnD).
 *
 * Phase 5c: render functions converted to Vue 3 native style (imported h,
 * flat data object, onXxx event keys) so RENDER_FUNCTION compat can be
 * flipped off.
 */

import { h } from 'vue'

const inFlight = new Map()

function uuid() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

const Drag = {
    name: 'Drag',
    props: {
        transferData: { default: null },
        // vue-drag-drop also accepts a `tag` prop to choose the wrapper element
        tag: { type: String, default: 'div' },
        draggable: { type: Boolean, default: true },
    },
    // Declare custom events so Vue 3 doesn't ALSO pass-through these
    // listeners as native DOM event listeners on the root element. Without
    // this, `@dragstart="handler"` would fire `handler` twice — once from
    // $emit('dragstart', transferData, e) and once from the native bubble
    // with only `(e)`, so two-arg handlers crash on the single-arg call.
    emits: ['dragstart', 'dragend'],
    render() {
        return h(this.tag, {
            draggable: String(this.draggable),
            onDragstart: (e) => {
                const id = uuid()
                inFlight.set(id, this.transferData)
                try {
                    e.dataTransfer.setData('text/plain', id)
                    e.dataTransfer.effectAllowed = 'move'
                } catch (err) { /* IE / restricted contexts */ }
                this.$emit('dragstart', this.transferData, e)
            },
            onDragend: (e) => {
                this.$emit('dragend', this.transferData, e)
                // Clean any leftover refs whose drop never fired
                setTimeout(() => {
                    for (const [k] of inFlight) inFlight.delete(k)
                }, 0)
            },
        }, this.$slots.default ? this.$slots.default() : null)
    },
}

const Drop = {
    name: 'Drop',
    props: {
        tag: { type: String, default: 'div' },
    },
    // See Drag.emits comment above — same Vue 3 fall-through fix.
    emits: ['drop', 'dragover', 'dragenter', 'dragleave'],
    render() {
        return h(this.tag, {
            onDragover: (e) => { e.preventDefault(); this.$emit('dragover', e) },
            onDragenter: (e) => { e.preventDefault(); this.$emit('dragenter', e) },
            onDragleave: (e) => this.$emit('dragleave', e),
            onDrop: (e) => {
                e.preventDefault()
                let data = null
                try {
                    const id = e.dataTransfer.getData('text/plain')
                    if (inFlight.has(id)) {
                        data = inFlight.get(id)
                        inFlight.delete(id)
                    }
                } catch (err) { /* noop */ }
                this.$emit('drop', data, e)
            },
        }, this.$slots.default ? this.$slots.default() : null)
    },
}

export default {
    install(app) {
        app.component('Drag', Drag)
        app.component('Drop', Drop)
    },
}
